import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useOxPay } from "@/hooks/useOxPay";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Loader2,
  AlertCircle,
  CheckCircle,
  UserPlus,
  Shield,
} from "lucide-react";

export default function OxPaySetup() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasAccount, userId, loading, createAccount } = useOxPay();
  const [setupStatus, setSetupStatus] = useState("checking"); // checking, ready, creating, success, error
  const [error, setError] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    // Check initial status
    if (!loading) {
      if (hasAccount && userId) {
        // Already has account - redirect to home
        setSetupStatus("success");
        setTimeout(() => {
          navigate("/home");
        }, 1500);
      } else {
        // Show setup screen
        setSetupStatus("ready");
      }
    }

    // Set a timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      if (loading) {
        console.warn("OxPay setup taking too long, skipping...");
        // If still loading after 5 seconds, assume API is down and skip
        navigate("/home");
      }
    }, 5000);

    return () => clearTimeout(timeout);
  }, [hasAccount, userId, loading, navigate]);

  const handleCreateAccount = async () => {
    setIsCreating(true);
    setSetupStatus("creating");
    setError("");

    try {
      // Extract user data for OxPay account
      const additionalData = {
        firstName: user?.user_metadata?.firstName || "",
        lastName: user?.user_metadata?.lastName || "",
        mobileNumber: user?.user_metadata?.mobileNumber || "",
        countryCode: user?.user_metadata?.countryCode || "US",
      };

      const result = await createAccount(additionalData);

      if (result.success) {
        setSetupStatus("success");
        // Small delay for success message
        setTimeout(() => {
          navigate("/home");
        }, 2000);
      } else {
        setSetupStatus("error");
        // Check if it's an API availability issue
        if (
          result.error?.includes("fetch") ||
          result.error?.includes("network")
        ) {
          setError(
            "Payment service is currently unavailable. You can skip this step and set up your payment account later."
          );
        } else {
          setError(result.error || "Failed to create payment account");
        }
        setIsCreating(false);
      }
    } catch (err) {
      setSetupStatus("error");
      setError("An unexpected error occurred. Please try again.");
      setIsCreating(false);
      console.error("OxPay setup error:", err);
    }
  };

  const handleSkip = () => {
    // User can skip for now but will be prompted again later
    navigate("/home");
  };

  // Loading state
  if (loading || setupStatus === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center space-y-4">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <p className="text-muted-foreground">
                Checking account status...
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Ready to create account
  if (setupStatus === "ready") {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
              <UserPlus className="h-8 w-8 text-primary" />
            </div>
            <CardTitle className="text-2xl">
              Set Up Your Payment Account
            </CardTitle>
            <CardDescription>
              To use FellasCard, we need to create a secure payment account for
              you. This only takes a moment.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-start space-x-3">
                <Shield className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-medium">Bank-level Security</p>
                  <p className="text-sm text-muted-foreground">
                    Your financial data is encrypted and secure
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-medium">Instant Setup</p>
                  <p className="text-sm text-muted-foreground">
                    One-click account creation with your existing info
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Button
                onClick={handleCreateAccount}
                className="w-full"
                disabled={isCreating}
              >
                {isCreating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  "Create Payment Account"
                )}
              </Button>
              <Button
                onClick={handleSkip}
                variant="ghost"
                className="w-full"
                disabled={isCreating}
              >
                Skip for Now
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Creating account
  if (setupStatus === "creating") {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center space-y-4">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <div className="text-center">
                <p className="font-medium">Creating your payment account...</p>
                <p className="text-sm text-muted-foreground mt-1">
                  This usually takes just a few seconds
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (setupStatus === "success") {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center space-y-4">
              <div className="h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <div className="text-center">
                <p className="text-xl font-semibold">Account Created!</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Your payment account is ready. Redirecting...
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (setupStatus === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
            <CardTitle>Setup Failed</CardTitle>
            <CardDescription className="text-red-600">{error}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Button
                onClick={handleCreateAccount}
                className="w-full"
                disabled={isCreating}
              >
                {isCreating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Retrying...
                  </>
                ) : (
                  "Try Again"
                )}
              </Button>
              <Button
                onClick={handleSkip}
                variant="outline"
                className="w-full"
                disabled={isCreating}
              >
                Skip for Now
              </Button>
            </div>
            <p className="text-xs text-center text-muted-foreground">
              If this problem persists, please contact support
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}
